'use client'
import { useState, useEffect } from 'react';
import { BiographyPage } from './biographyPage';
import { useFormContext } from './formContext';
import { SocialMediaInfo } from './socialMediaInfo';
import { MediaEvidence } from './mediaEvidence';
import { ContactInfo } from './contactInfo';
import SvgIcon from '../layout/svg-icons/svg-icons'
import Link from 'next/link';

export enum SubmissionSteps {
  Biographical = 0,
  SocialMediaInfo = 1,
  MediaEvidence = 2,
  Contact = 3
}

export type CompletionStatus = {
  [key in SubmissionSteps]: boolean
};

export const ReportIndividual = () => {

  const initialCompletionStatus: CompletionStatus = {
    [SubmissionSteps.Biographical]: false,
    [SubmissionSteps.SocialMediaInfo]: false,
    [SubmissionSteps.MediaEvidence]: false,
    [SubmissionSteps.Contact]: false,
  };

  const { resetForm } = useFormContext();

  const [currentStep, setCurrentStep] = useState<SubmissionSteps>(SubmissionSteps.Biographical);
  const [completionStatus, setCompletionStatus] = useState<CompletionStatus>(initialCompletionStatus);

  const [isSubmitted, setIsSubmitted] = useState(false);


  useEffect(() => {
    window.scrollTo(0, 0)
  }, [isSubmitted])

  const renderCurrentStepIcon = (h: string, w: string) => {
    return (
      <SvgIcon
        width={w}
        height={h}
        viewBox="0 0 22 22"
        fill="none"
      >
        <circle cx="11" cy="11" r="8" fill="white" stroke="#1A56DB" strokeWidth="6" />
      </SvgIcon>
    )
  };

  const renderIncompleteStep = (h: string, w: string) => {
    return (
      <SvgIcon width={w} height={h} viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="10" transform="rotate(-90 11 11)" fill="white" stroke="var(--tip-form-blue)" strokeWidth="2" />
      </SvgIcon>
    )
  };

  const renderUpcomingStep = (h: string, w: string) => {
    return (
      <SvgIcon width={w} height={h} viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="10" transform="rotate(-90 11 11)" fill="white" stroke="#D1D5DB" strokeWidth="2" />
      </SvgIcon>
    )
  };

  const renderCompletedIcon = (h: string, w: string) => {
    return (
      <div
        className={`rounded-full border-2 p-0.5 border-tipFormBlue flex items-center justify-center`}
        style={{ width: `${w}px`, height: `${h}px` }}
      >
        <SvgIcon width={w} height={h} viewBox="0 0 14 12" fill="none">
          <path d="M5.00878 11C4.7803 11.0009 4.56052 10.9089 4.39647 10.7437L0.264208 6.57747C0.181845 6.49392 0.116126 6.39434 0.0708037 6.2844C0.0254817 6.17446 0.00144401 6.05633 6.31568e-05 5.93674C-0.00272561 5.69522 0.086941 5.46244 0.249337 5.28961C0.411734 5.11678 0.633557 5.01806 0.866009 5.01516C1.09846 5.01226 1.3225 5.10543 1.48884 5.27416L5.01228 8.82508L12.5105 1.25881C12.6771 1.09007 12.9014 0.997006 13.134 1.00007C13.3666 1.00314 13.5885 1.1021 13.7509 1.27517C13.9133 1.44824 14.0029 1.68125 13.9999 1.92294C13.997 2.16463 13.9017 2.3952 13.7352 2.56393L5.6211 10.7437C5.45705 10.9089 5.23727 11.0009 5.00878 11Z" fill="#1A56DB" />
        </SvgIcon>
      </div>
    )
  };

  const markStepAsCompleted = (step: SubmissionSteps, status: boolean = true) => {
    setCompletionStatus({ ...completionStatus, [step]: status });
  };

  const renderStepIcon = (step: SubmissionSteps, h: string, w: string) => {
    if (step === currentStep) return renderCurrentStepIcon(h, w);
    if (completionStatus[step]) return renderCompletedIcon(h, w);
    if (step < currentStep) return renderIncompleteStep(h, w);
    return renderUpcomingStep(h, w);
  };

  const goToStep = (step: SubmissionSteps) => {
    if (step < currentStep) {
      setCurrentStep(step);
    }
  }

  const renderMenuItem = (step: SubmissionSteps, title: string, hideLineSpacer: boolean = false) => {
    return (
      <>
        <div
          className={`flex text-sm leading-5 w-full font-medium flex-row items-center ${step < currentStep ? 'cursor-pointer' : ''}`}
          onClick={() => goToStep(step)}
        >
          {renderStepIcon(step, '22', '22')}
          <div className='ml-1'>{title}</div>
        </div>
        {hideLineSpacer ? null : <div className='h-[76px] bg-gray-300 w-0.5 ml-2.5' />}
      </>
    );
  };

  const renderSmallMenuItem = (step: SubmissionSteps, title: string, hideLineSpacer: boolean = false) => {
    return (
      <div className="flex flex-grow flex-col">
        <div className={`flex flex-row items-center ${step < currentStep ? 'cursor-pointer' : ''}`} onClick={() => goToStep(step)}>
          <div className="flex">
            {renderStepIcon(step, '20', '20')}
          </div>
          {hideLineSpacer ? null : <div className={`h-0.5 bg-gray-300 flex-grow min-w-[50px]`} />}
        </div>
        <div className="ml-1 mt-2 text-xs font-medium leading-4">
          {step === currentStep ? title : null}
        </div>
      </div>
    );
  };

  const renderMenu = () => {
    return (
      <div className='hidden md:flex flex-col min-w-[255px] mt-[10px] pl-[20px]'>
        {renderMenuItem(SubmissionSteps.Biographical, 'Basic Details')}
        {renderMenuItem(SubmissionSteps.SocialMediaInfo, 'Social Media Information')}
        {renderMenuItem(SubmissionSteps.MediaEvidence, 'Evidence')}
        {renderMenuItem(SubmissionSteps.Contact, 'Contact Information', true)}
      </div>
    );
  };

  const renderSmallMenu = () => {
    return (
      <div className='flex w-full md:hidden flex-row my-[30px]'>
        {renderSmallMenuItem(SubmissionSteps.Biographical, 'Basic Details')}
        {renderSmallMenuItem(SubmissionSteps.SocialMediaInfo, 'Social Media Information')}
        {renderSmallMenuItem(SubmissionSteps.MediaEvidence, 'Evidence')}
        {renderSmallMenuItem(SubmissionSteps.Contact, 'Contact Information', true)}
      </div>
    );
  }

  const addAnotherTip = () => {
    setCurrentStep(SubmissionSteps.Biographical);
    setIsSubmitted(false);
    setCompletionStatus(initialCompletionStatus);
    resetForm();
  }

  const renderFormContent = () => {
    if (isSubmitted) {
      return (
        <div className={"h-screen max-[1200px]:flex max-[1200px]:flex-col max-[1200px]:items-center"}>
          <div className='text-[30px] font-bold leading-[45px]'>
            Thank you for your tip
          </div>
          <div className={'flex h-[70px] w-[70px] mt-2 md:hidden'}>{renderCompletedIcon('70', '70')}</div>
          <div className='text-[16x] text-center md:text-left mb-[10px] font-medium leading-[24px] my-4'>
            <div className='mb-3'>Your profile tip form has been submitted.</div>
            <div className='mb-3'>
              We appreciate you taking the time to fill out the profile tip form, and join our fight against hatred.
            </div>
            <div>
              If you provided an email address, you will be contacted about the status of your submission.
            </div>
            <div className={'flex flex-row justify-center align-center my-3 p-3 w-full border-t border-gray-300'}>
              <button
                className={'rounded-lg flex justify-center items-center w-[200px] h-[35px] bg-black !text-white font-medium leading-6 mt-2'}
                aria-label={'submit again'}
                onClick={() => addAnotherTip()}
              >
                Submit another Tip
              </button>
            </div>
          </div>
        </div>
      );
    };
    switch (currentStep) {
      case SubmissionSteps.Biographical:
      default:
        return (
          <BiographyPage
            setCurrentStep={setCurrentStep}
            markStepAsCompleted={markStepAsCompleted}
            currentStep={currentStep}
          />);

      case SubmissionSteps.SocialMediaInfo:
        return (
          <SocialMediaInfo
            setCurrentStep={setCurrentStep}
            markStepAsCompleted={markStepAsCompleted}
            currentStep={currentStep}
          />);

      case SubmissionSteps.MediaEvidence:
        return (
          <MediaEvidence
            setCurrentStep={setCurrentStep}
            markStepAsCompleted={markStepAsCompleted}
            currentStep={currentStep}
          />);
      case SubmissionSteps.Contact:
        return (
          <ContactInfo
            setCurrentStep={setCurrentStep}
            markStepAsCompleted={markStepAsCompleted}
            currentStep={currentStep}
            setIsSubmitted={setIsSubmitted}
          />);
    }
  }

  return (
    <div className='w-full'>
      <div className="text-[30px] font-bold leading-[45px] md:text-[40px] md:leading-[60px] md:mb-[25px] border-b-[1px] border-b-gray-600">
        <div className='mb-[10px] text-center'>Submission Form for Individuals</div>
        <div className='text-[14px] text-center font-normal leading-[21px] mb-[5px]'> For general inquiries, or ex-Canary requests, please
          <Link
            className="text-colorLink no-underline mx-1"
            href="/contact_us"
          >
            contact us
          </Link>.
        </div>
        <div className='text-[14px] mb-[10px] text-center font-normal leading-[21px]'>To report an antisemitic event, please go to
          <Link
            className="text-colorLink no-underline ml-1"
            href="/report_an_event"
          >
            report event
          </Link>.
        </div>
      </div>
      <div className='flex flex-col md:flex-row'>
        {renderMenu()}
        {renderSmallMenu()}
        <div className='md:ml-4 w-full md:w-[650px]'>
          {renderFormContent()}
        </div>
      </div>
    </div>
  )
}